import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { UserCog, UserPlus, Loader2, ShieldCheck, Receipt, PackagePlus, UserCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { id: "agent", label: "Agent", desc: "Can only create bills (POS, Customers, Invoices)", icon: Receipt, cls: "bg-sky-100 text-sky-800 hover:bg-sky-100" },
  { id: "manager", label: "Manager", desc: "Billing + add medicine + add stock + suppliers + reports", icon: PackagePlus, cls: "bg-amber-100 text-amber-800 hover:bg-amber-100" },
  { id: "admin", label: "Admin", desc: "Full access — settings, staff, everything", icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  { id: "hr", label: "HR", desc: "Attendance, salary/payroll and salary slips only", icon: UserCheck, cls: "bg-violet-100 text-violet-800 hover:bg-violet-100" },
  { id: "ca", label: "CA (Accountant)", desc: "Sirf CA Report — GST sales register + purchases, read-only", icon: Receipt, cls: "bg-indigo-100 text-indigo-800 hover:bg-indigo-100" },
];

const roleBadge = (r) => ROLES.find((x) => x.id === r) || { label: r, cls: "bg-slate-100 text-slate-700 hover:bg-slate-100" };

export default function Staff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [salary, setSalary] = useState("");
  const [role, setRole] = useState("agent");
  const [saving, setSaving] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error("Fill all fields");
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role, salary: Number(salary) || 0 });
      toast.success(`${name}'s ${role} login created`);
      setName(""); setEmail(""); setPassword(""); setSalary(""); setRole("agent");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = (u) => {
    if (u.is_active !== false) setConfirmUser(u);
    else void applyToggle(u, false);
  };

  const applyToggle = async (u, deactivating) => {
    try {
      if (deactivating) await api.delete(`/users/${u.id}`);
      else await api.put(`/users/${u.id}`, { is_active: true });
      toast.success(deactivating ? `${u.name} deactivated` : `${u.name} re-activated`);
      setConfirmUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const submitPw = async () => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    setPwSaving(true);
    try {
      await api.post(`/users/${pwFor.id}/reset-password`, { password: newPw });
      toast.success(`Password updated for ${pwFor.name}`);
      setPwFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="staff-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
          <UserCog className="w-6 h-6 text-emerald-700" /> Staff Management
        </h1>
        <p className="text-sm text-slate-500">Create logins for agents and managers, and control their access.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Card className="lg:col-span-2 p-6 border-slate-200 h-fit">
          <h3 className="font-display font-bold flex items-center gap-2"><UserPlus className="w-4 h-4 text-emerald-700" /> Create New Login</h3>
          <form onSubmit={create} className="space-y-3 mt-4">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input data-testid="staff-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" placeholder="e.g. Ramesh Kumar" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input data-testid="staff-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" placeholder="agent@trustmedspharma.com" />
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input data-testid="staff-password-input" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" placeholder="Min 6 characters" />
            </div>
            <div>
              <Label className="text-xs">Monthly Salary (₹)</Label>
              <Input data-testid="staff-salary-input" type="number" min="0" value={salary} onChange={(e) => setSalary(e.target.value)} className="mt-1 h-11" placeholder="15000" />
            </div>
            <div>
              <Label className="text-xs">Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="staff-role-select" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ROLES.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`role-option-${r.id}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">{ROLES.find((r) => r.id === role)?.desc}</p>
            </div>
            <Button type="submit" disabled={saving} data-testid="create-staff-btn" className="w-full h-11 bg-emerald-900 hover:bg-emerald-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Login
            </Button>
          </form>
        </Card>

        <div className="lg:col-span-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ROLES.map((r) => (
              <Card key={r.id} className="p-4 border-slate-200">
                <div className="flex items-center gap-2">
                  <r.icon className="w-4 h-4 text-emerald-700" />
                  <span className="font-semibold text-sm">{r.label}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">{r.desc}</p>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <Table data-testid="staff-table">
                <TableHeader><TableRow>
                  <TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Salary</TableHead><TableHead>Joined</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const rb = roleBadge(u.role);
                    return (
                      <TableRow key={u.id} data-testid={`staff-row-${u.id}`} className={u.is_active === false ? "opacity-60" : ""}>
                        <TableCell>
                          <div className="font-semibold">{u.name} {u.id === me?.id && <span className="text-xs text-emerald-700">(you)</span>}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </TableCell>
                        <TableCell><Badge className={rb.cls}>{rb.label}</Badge></TableCell>
                        <TableCell>
                          <Badge data-testid={`staff-status-${u.id}`} className={u.is_active === false ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}>
                            {u.is_active === false ? "Inactive" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono-num">{u.salary > 0 ? `₹${Number(u.salary).toLocaleString("en-IN")}` : "—"}</TableCell>
                        <TableCell className="text-sm text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</TableCell>
                        <TableCell className="text-right">
                          {u.id !== me?.id && (
                            <div className="flex items-center justify-end gap-2">
                              <Switch
                                checked={u.is_active !== false}
                                onCheckedChange={() => toggleActive(u)}
                                data-testid={`toggle-staff-${u.id}`}
                              />
                              <Button size="icon" variant="ghost" onClick={() => { setPwFor(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`} title="Change password">
                                <KeyRound className="w-4 h-4 text-slate-600" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No staff yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </div>

      <AlertDialog open={!!confirmUser} onOpenChange={(o) => !o && setConfirmUser(null)}>
        <AlertDialogContent className="bg-white" data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Deactivate {confirmUser?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmUser?.email} ka login band ho jayega, par unki sales, attendance aur salary history intact rahegi. Kabhi bhi toggle se re-activate kar sakte hain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="deactivate-confirm" onClick={() => applyToggle(confirmUser, true)} className="bg-red-600 hover:bg-red-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Change password — {pwFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{pwFor?.email} ke liye naya password set karein. Purana password aur active sessions turant band ho jayenge.</p>
            <div>
              <Label className="text-xs">New Password *</Label>
              <Input data-testid="reset-pw-input" type="password" minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1 h-11" placeholder="Min 6 characters" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwFor(null)}>Cancel</Button>
            <Button onClick={submitPw} disabled={pwSaving} data-testid="reset-pw-confirm" className="bg-emerald-900 hover:bg-emerald-800">
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
